import { useEffect, useRef } from 'react';
import { Badge, Button, ErrorState, LoadingState } from '../../components';
import { useResultDetail } from '../../hooks/use-searches';
import { describeError } from '../../services/errors';
import { formatCount, formatDateTime } from './ResultCard';
import { explainRelevance } from './relevance-labels';

/**
 * Detalhe do resultado (§17).
 *
 * Mostra o histórico de coleta — de onde o dado veio e quando — porque
 * auditabilidade é requisito do produto. Não mostra id externo nem ator do
 * fornecedor: é detalhe técnico sem valor para quem pesquisa.
 */
export function ResultDetailPanel({
  resultId,
  onClose,
}: {
  resultId: string;
  onClose: () => void;
}) {
  const detail = useResultDetail(resultId);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Foco vai para dentro do painel ao abrir e Esc fecha: sem isso o teclado
    // ficaria preso atrás do overlay (RNF-F05).
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-10 flex justify-end bg-overlay p-0 sm:p-4">
      <button
        type="button"
        aria-label="Fechar detalhe"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Detalhe do resultado"
        className="relative z-10 flex w-full max-w-xl flex-col overflow-y-auto rounded-lg border border-border bg-surface p-5 shadow-md"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-text">Detalhe do resultado</h2>
          <Button ref={closeRef} variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>

        {detail.isPending && <LoadingState label="Carregando detalhe" />}
        {detail.isError && <ErrorState message={describeError(detail.error)} />}

        {detail.data && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="info">{detail.data.platform}</Badge>
              <Badge>{detail.data.type}</Badge>
              <span className="text-text-muted">{formatDateTime(detail.data.publishedAt)}</span>
            </div>

            <div>
              <p className="font-medium text-text">
                {detail.data.author.displayName ??
                  detail.data.author.username ??
                  'Autor desconhecido'}
              </p>
              {detail.data.author.username && (
                <p className="text-xs text-text-muted">@{detail.data.author.username}</p>
              )}
            </div>

            <p className="whitespace-pre-line text-text">
              {detail.data.text ?? 'Publicação sem texto.'}
            </p>

            {detail.data.hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {detail.data.hashtags.map((tag) => (
                  <Badge key={tag}>#{tag.replace(/^#/, '')}</Badge>
                ))}
              </div>
            )}

            <dl className="grid grid-cols-2 gap-3 rounded-md bg-surface-raised p-3 sm:grid-cols-4">
              <Metric label="Curtidas" value={formatCount(detail.data.engagement.likes)} />
              <Metric label="Comentários" value={formatCount(detail.data.engagement.comments)} />
              <Metric label="Compartilh." value={formatCount(detail.data.engagement.shares)} />
              <Metric label="Visualizações" value={formatCount(detail.data.engagement.views)} />
            </dl>

            <a
              href={detail.data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent hover:underline"
            >
              Abrir publicação original
            </a>

            {/* Por que este resultado está onde está (RF-F09). */}
            {explainRelevance(detail.data.relevanceSignals) !== '' && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-text">Por que apareceu aqui</h3>
                <p className="rounded-md border border-border px-3 py-2 text-xs text-text-muted">
                  {explainRelevance(detail.data.relevanceSignals)}
                </p>
              </section>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-text">Origem do dado</h3>
              <ul className="flex flex-col gap-2">
                {detail.data.collection.map((source, index) => (
                  <li
                    key={`${source.provider}-${index}`}
                    className="rounded-md border border-border px-3 py-2 text-xs text-text-muted"
                  >
                    Coletado via <span className="text-text">{source.provider}</span> em{' '}
                    {formatDateTime(source.collectedAt)}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-sm font-medium text-text">{value}</dd>
    </div>
  );
}
